/**
 * Curated synonyms for the destination index, keyed by route.
 *
 * This file is the difference between a search that works and one that looks
 * like it works. A director types the word from her job, not the word on our
 * button: "time off" for a page labelled PTO, "fire drill" for the Drill Log,
 * "shots" for immunization records, "cancel" for Subscription. None of those
 * are reachable by matching labels, however clever the matcher.
 *
 * Keep entries in the user's vocabulary, not ours. Add freely — an unused
 * keyword costs nothing, a missing one costs a support email.
 */

/** Synonyms for a whole hub, applied to the hub's own tile in results. */
export const HUB_KEYWORDS: Record<string, string[]> = {
  directors: ['task', 'tasks', 'to do', 'todo', 'checklist', 'daily duties', 'routine', 'director'],
  finance: ['money', 'accounting', 'bookkeeping', 'spending', 'revenue', 'cost', 'financial'],
  documents: ['paperwork', 'files', 'records', 'forms', 'documentation'],
  orientation: ['sop', 'handbook', 'procedures', 'playbook', 'training', 'onboarding'],
  eval_student: ['child', 'children', 'kids', 'student', 'behavior'],
  eval_teacher: ['staff', 'teacher', 'employee', 'performance', 'evaluation'],
  feedback: ['survey', 'parent feedback', 'questionnaire', 'reviews'],
  staffing: ['schedule', 'shifts', 'roster', 'coverage', 'ratio', 'ratios', 'staffing', 'scheduling'],
  compliance: ['licensing', 'inspection', 'state', 'regulations', 'audit', 'license'],
  resources: ['ai', 'chat', 'assistant', 'eddy', 'ask', 'artificial intelligence'],
  a4u: ['store', 'buy', 'purchase', 'shop', 'templates'],
};

/** Synonyms for a specific page. */
export const PATH_KEYWORDS: Record<string, string[]> = {
  // The headline case: PTO lives in two hubs under two different names.
  '/documents/pto': ['time off', 'vacation', 'sick days', 'leave', 'absence', 'days off', 'paid time off', 'request off', 'pto balance'],
  '/documents/pto-setup': ['time off settings', 'pto accrual', 'vacation policy'],
  '/staffing/pto': ['pto', 'vacation', 'absence', 'who is off', 'time off calendar'],

  '/documents/students': ['immunization', 'shots', 'physical', 'medical', 'child records', 'student files', 'enrollment forms'],
  '/documents/teachers': ['staff files', 'employee files', 'background check', 'tb test', 'fingerprints'],
  '/documents/trainings': ['training hours', 'ceu', 'professional development', 'in service', 'clock hours'],
  '/documents/packets': ['forms', 'packet', 'new family paperwork', 'enrollment packet'],
  '/documents/parent-links': ['parent upload', 'share link', 'send to parent'],

  '/finance/income': ['revenue', 'tuition', 'payments', 'deposits'],
  '/finance/expense': ['bills', 'receipts', 'purchases', 'spending', 'costs'],
  '/finance/mileage': ['miles', 'driving', 'gas', 'travel', 'vehicle'],
  '/finance/budget': ['forecast', 'plan spending'],
  '/finance/import': ['csv', 'upload transactions', 'bulk import', 'bank export'],
  '/finance/classroom-spending': ['classroom budget', 'teacher spending', 'room supplies'],

  '/compliance/expirations': ['cpr', 'first aid', 'credential', 'certification', 'expiring', 'renewal', 'certificates'],
  '/compliance/drills': ['fire drill', 'tornado', 'drill log', 'evacuation', 'lockdown', 'emergency drill'],
  '/compliance/food-program': ['cacfp', 'food program', 'meals', 'nutrition'],
  '/compliance/food-program/menus': ['menu', 'meal plan', 'what we serve'],
  '/compliance/food-program/meal-counts': ['meal count', 'attendance for meals', 'claim'],
  '/compliance/citations': ['violation', 'corrective action', 'cap', 'cited', 'deficiency'],
  '/compliance/binder': ['inspection binder', 'print binder', 'licensing binder'],
  '/compliance/walk-in': ['walkthrough', 'self inspection', 'spot check'],
  '/compliance/emergency': ['emergency plan', 'disaster', 'evacuation plan'],
  '/compliance/classrooms': ['classroom audit', 'room check'],
  '/compliance/rules': ['licensing rules', 'regulations', 'requirements', 'state rules'],
  '/compliance/vault': ['evidence', 'proof', 'upload evidence'],

  '/staffing/schedule': ['staff schedule', 'shifts', 'who works', 'work schedule'],
  '/staffing/students': ['student schedule', 'child schedule', 'attendance', 'sign in sheet'],
  '/staffing/enrollment': ['room assignment', 'move up', 'transition', 'classroom placement'],
  '/staffing/agenda': ['daily agenda', 'activities', 'daily plan'],
  '/staffing/lesson-plans': ['lesson plan', 'curriculum', 'weekly plan', 'supplies'],
  '/staffing/template': ['recurring schedule', 'default schedule'],
  '/staffing/settings': ['center hours', 'open close', 'holidays', 'closures'],

  '/eval-teacher/observations': ['classroom observation', 'walkthrough', 'observe'],
  '/eval-teacher/disciplinary': ['write up', 'write-up', 'discipline', 'infraction', 'warning', 'corrective'],
  '/eval-teacher/point-system': ['points', 'infraction points', 'demerits'],
  '/eval-teacher/pips': ['pip', 'performance improvement plan'],

  '/eval-student/incident-reports': ['incident', 'accident', 'injury', 'boo boo', 'ouch report', 'bump'],
  '/eval-student/behavior-reports': ['behavior report', 'biting', 'behavior log'],
  '/eval-student/behavior-plans': ['behavior plan', 'intervention'],
  '/eval-student/conferences': ['parent conference', 'parent meeting', 'family meeting'],

  '/directors/inquiries': ['call inquiry', 'tour', 'prospective parent', 'waitlist', 'phone call', 'lead'],
  '/directors/notes': ['notes', 'task notes'],
  '/directors/stickies': ['sticky note', 'reminder', 'post it'],

  '/playbook/onboarding': ['new hire', 'new staff', 'orientation'],
  '/playbook/sops': ['sop', 'procedure', 'handbook', 'how we do things'],
  '/playbook/certificates': ['certificate', 'completion certificate'],

  '/feedback/surveys': ['parent survey', 'send survey', 'questionnaire'],

  '/calendar': ['everything', 'all dates', 'whats coming up', 'agenda'],

  '/settings': ['profile', 'password', 'my name', 'email', 'appearance', 'theme', 'account'],
  '/settings/schools': ['add school', 'center info', 'logo', 'school name', 'second location'],
  '/settings/subscriptions': ['billing', 'cancel', 'plan', 'upgrade', 'downgrade', 'invoice', 'payment', 'credit card', 'price', 'pricing', 'add a hub', 'renew'],
};
